import React from 'react';
import { TVLoadProps } from 'app/components/dashboard/network/flows/TVLoad';
import { Menu } from 'antd';
import { ExtendedMonitoredTv, MonitoredMeasure } from 'app/model/SOL49';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { RegulationPeriods } from 'app/model/client/CORE';
import { MonitoredLoadValue, RegulationOrMcdmOnlyDto } from 'app/model/CORE';
import MeasureUtils from 'app/common/util/MeasureUtils';
import memoizeOne from 'memoize-one';
import {
    AddToCustomLayerEvent,
    NewCustomLayerEvent,
    OpenTvMonitoredEvents,
    RemoveFromCustomLayerEvent,
    RemoveFromWorkspaceLayerEvent,
    ShowModalEvent,
    ShowTvDetailEvent,
} from 'app/common/eventbus/Events';
import { CustomLayerUtils, MapLayer, MapLayerUtils, MapObjectType } from 'app/components/dashboard/network/map/Map';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';
import AppContext from 'app/common/context/AppContext';
import { Flex } from 'app/components/common/Flex';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import Utils from 'app/common/util/Utils';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { MonitoredEventType, MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import { HotspotPanel } from 'app/components/panels/measure/HotspotPanel';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { ApplicableScenarioMenuUtils, ScenarioMenuState } from '../../../common/ApplicableScenarioMenuUtils';
import { RootState } from '../../../../../redux/store';
import {
    addToFlowSection,
    createFlowSection,
    removeFromFlowSection,
    selectAllFlows,
    selectUserCustomFlows,
} from '../../../../../redux/features/dashboard/user-monitored-flows-slice';
import { connect, ConnectedProps } from 'react-redux';
import { MenuUtils } from '../../../common/MenuUtils';

const ACTION_SHOW_DETAILS = 'show-details';
const ACTION_CREATE_NEW_REGULATION = 'create-new-regulation';
const ACTION_CREATE_NEW_HOTSPOT = 'create-new-hotspot';
const ACTION_FIND_SOLUTION = 'find-solution';
const ACTION_ADD_NOTE = 'add-note';
const ACTION_ADD_EVENT = 'add-event';
const ACTION_CREATE_CUSTOM_LAYER = 'create-custom-layer';

const ACTIONS = [
    ACTION_SHOW_DETAILS,
    ACTION_CREATE_NEW_REGULATION,
    ACTION_CREATE_NEW_HOTSPOT,
    ACTION_FIND_SOLUTION,
    ACTION_ADD_EVENT,
    ACTION_ADD_NOTE,
    ACTION_CREATE_CUSTOM_LAYER,
];

const MapTVContextMenuStateToProps = (state: RootState) => {
    return {
        allFlowsSections: selectAllFlows(state),
        customFlowSections: selectUserCustomFlows(state),
    };
};
const MapTVContextMenuMapDispatchToProps = {
    createFlowSection,
    addToFlowSection,
};

interface MapTVContextMenuPropsOwn extends ObjectTypeContextMenu {
    tvs: Array<TVLoadProps>; //TODO: remove
    monitoredTvList: Array<ExtendedMonitoredTv>;
    workspaceTvIds: Array<string>;

    onClose(): void;
}

const connector = connect(MapTVContextMenuStateToProps, MapTVContextMenuMapDispatchToProps);
type MapTVContextMenuPropsFromRedux = ConnectedProps<typeof connector>;

type MapTVContextMenuProps = MapTVContextMenuPropsOwn & MapTVContextMenuPropsFromRedux;

interface MapTVContextMenuState extends ScenarioMenuState {}
class MapTVContextMenuComponent extends React.PureComponent<MapTVContextMenuProps, MapTVContextMenuState> {
    constructor(props: MapTVContextMenuProps) {
        super(props);
        this.state = {
            isApplicableScenarioLoading: false,
        };
    }

    render() {
        return (
            <Menu
                key="map-tv-context-menu"
                items={this.getMenuItems()}
                onClick={this.handleOnClick}
                triggerSubMenuAction="click"
            />
        );
    }

    getMenuItems = () => {
        const subMenu = this.props.monitoredTvList?.map(extendedMonitoredTv => {
            const tvLoad = this.props.tvs.find(tvLoad => tvLoad.tvName === extendedMonitoredTv.refLocation.id);
            const notes = tvLoad.events?.filter(MonitoredEventUtils.isNoteEvent);
            const events = tvLoad.events?.filter(MonitoredEventUtils.isWeatherEvent);
            let tvMenuEntries = undefined;
            if (!AuthorisationUtils.isFmpMapUser()) {
                tvMenuEntries = [
                    {
                        label: extendedMonitoredTv.refLocation.id,
                        key: `tvload-${extendedMonitoredTv.refLocation.id}`,
                        disabled: true, //otherwise when clicking it hides the menu
                    },
                    { key: 'divider-1', type: 'divider' },
                    {
                        label: 'Show details (graph)',
                        key: this.actionKey(ACTION_SHOW_DETAILS, extendedMonitoredTv.refLocation.id),
                    },
                    { key: 'divider-2', type: 'divider' },
                    {
                        label: 'Create a new regulation',
                        key: this.actionKey(ACTION_CREATE_NEW_REGULATION, extendedMonitoredTv.refLocation.id),
                    },
                    {
                        label: 'Create a new hotspot',
                        key: this.actionKey(ACTION_CREATE_NEW_HOTSPOT, extendedMonitoredTv.refLocation.id),
                    },
                    ApplicableScenarioMenuUtils.applicableScenarioMenu(
                        [extendedMonitoredTv.refLocation.id],
                        null,
                        this.state,
                        (state: MapTVContextMenuState) => this.setState(state),
                    ),
                    {
                        label: 'Find solution',
                        key: this.actionKey(ACTION_FIND_SOLUTION, extendedMonitoredTv.refLocation.id),
                    },
                    { key: 'divider-3', type: 'divider' },
                    MenuUtils.addToCustomSectionMenu(
                        this.props.customFlowSections,
                        payload => this.props.addToFlowSection(payload),
                        payload => this.props.createFlowSection(payload),
                        [extendedMonitoredTv.refLocation.id],
                        'queryPanel',
                    ),
                    { key: 'divider-4', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_CREATE_CUSTOM_LAYER, extendedMonitoredTv.refLocation.id),
                        label: 'Create a custom layer',
                    },
                    this.addToCustomLayer(extendedMonitoredTv.refLocation.id),
                    ...this.removeFromCustomLayerMenu(extendedMonitoredTv.refLocation.id),
                    this.removeFromWorkspaceLayer(extendedMonitoredTv.refLocation.id),
                    { key: 'divider-5', type: 'divider' },
                    {
                        key: this.actionKey(ACTION_ADD_NOTE, extendedMonitoredTv.refLocation.id),
                        label: `${Utils.isNotEmpty(notes) ? 'Show notes' : 'Add a note'}`,
                    },
                    {
                        key: this.actionKey(ACTION_ADD_EVENT, extendedMonitoredTv.refLocation.id),
                        label: `${Utils.isNotEmpty(events) ? 'Show events' : 'Add an event'}`,
                    },
                ];
            }
            return {
                label: this.getTvNameLabel(extendedMonitoredTv),
                key: extendedMonitoredTv.refLocation.id,
                className: 'menuItemConvection',
                children: tvMenuEntries,
            };
        });
        if (this.props.group) {
            return [
                {
                    label: <span className="detailed-subpanel-title">TFV ({this.props.monitoredTvList.length})</span>,
                    key: 'tfvs',
                    children: subMenu,
                },
            ];
        } else {
            return subMenu;
        }
    };

    private getTvNameLabel(monitoredTv: ExtendedMonitoredTv) {
        const timePeriod = this.getMonitoredPeriod(monitoredTv.loadValues);
        return (
            <div onClick={event => this.handleTvLoadClick(event, monitoredTv.refLocation.id)}>
                <Flex grow={false}>
                    <Flex size="6.5em">
                        TV: <span style={{ color: 'white' }}>{monitoredTv.refLocation.id}</span>
                    </Flex>
                    <Flex direction="vertical" style={{ alignSelf: 'center', paddingLeft: '1em' }}>
                        {monitoredTv?.loadValues && (
                            <EntryLoadBar
                                key="entry-load"
                                loadValues={monitoredTv.loadValues}
                                monitoredPeriod={timePeriod}
                                activePeriods={monitoredTv.activePeriods}
                                currentTime={AppContext.getCurrentTime()}
                                drawBottomLine={true}
                                barHeightEm={0.5}
                            />
                        )}
                        {monitoredTv?.occupancyLoadValues &&
                            monitoredTv?.occupancyLoadValues.map(occupancyValues => {
                                return (
                                    <OccupancyLoadBar
                                        key={'occupancy-load-' + occupancyValues.durationInMin}
                                        loadValues={occupancyValues.occupancyLoadValues}
                                        monitoredPeriod={timePeriod}
                                        activePeriods={monitoredTv.activePeriods}
                                        currentTime={AppContext.getCurrentTime()}
                                        drawBottomLine={true}
                                        barHeightEm={0.5}
                                    />
                                );
                            })}
                    </Flex>
                </Flex>
            </div>
        );
    }

    handleTvLoadClick(event: React.MouseEvent<HTMLDivElement>, tvName: string) {
        event.stopPropagation();
        this.props.onClose();
        ShowTvDetailEvent.fireEvent(tvName);
    }

    /**
     * Extract monitored period from load values
     */
    getMonitoredPeriod = memoizeOne((loadValues: Array<MonitoredLoadValue>) => {
        return MonitoredLoadUtils.getMonitoredPeriod(loadValues);
    });

    removeFromCustomLayerMenu(tvName: string) {
        const tvCustomLayers = this.getTvCustomLayers(tvName);
        if (tvCustomLayers?.length > 0) {
            return [
                { key: 'divider-' + tvName, type: 'divider' },
                {
                    key: 'remove-from-layer-' + tvName,
                    label: 'Remove from custom layer',
                    children: [
                        ...tvCustomLayers.map(customLayer => ({
                            key: 'remove-from-layer-' + customLayer.id + tvName,
                            label: `Remove from: ${customLayer.label}`,
                            onClick: () => this.removeFromCustomLayerMenuHandleUpdateSection(tvName, customLayer),
                        })),
                    ],
                },
            ];
        }
        return [];
    }

    addToCustomLayer(tvName: string) {
        const result = {
            label: 'Add to custom layer',
            key: 'add-to-custom-layer-menu-' + tvName,
            children: [
                ...CustomLayerUtils.customLayers
                    .filter(customLayer => !CustomLayerUtils.containsTv(customLayer, tvName))
                    .map(customLayer => ({
                        key: 'add-to-layer-' + customLayer.id + tvName,
                        label: `Add to: ${customLayer.label}`,
                        onClick: () => this.addToCustomLayerMenuHandleUpdateSection(tvName, customLayer),
                    })),
            ],
        };

        if (result.children.length > 0) {
            return result;
        }
    }

    removeFromWorkspaceLayer(tvName: string) {
        if (Utils.contains(this.props.workspaceTvIds, tvName)) {
            return {
                key: 'remove-from-workspace-layer-' + tvName,
                label: 'Remove from workspace layer',
                onClick: () => this.removeFromWorkspaceLayerMenuHandle(tvName),
            };
        }
    }

    getTvCustomLayers(tvName: string) {
        return CustomLayerUtils.customLayers.filter(
            customLayer =>
                customLayer.objectsToDisplay.filter(
                    objectToDisplay =>
                        MapObjectType.TV === objectToDisplay.type &&
                        tvName === MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(objectToDisplay.object),
                )?.length > 0,
        );
    }

    addToCustomLayerMenuHandleUpdateSection(tvName: string, customLayer: MapLayer) {
        this.props.onClose();
        AddToCustomLayerEvent.fire(customLayer, tvName, MapObjectType.TV);
    }

    removeFromCustomLayerMenuHandleUpdateSection(tvName: string, customLayer: MapLayer) {
        this.props.onClose();
        RemoveFromCustomLayerEvent.fire(customLayer, tvName, MapObjectType.TV);
    }

    removeFromWorkspaceLayerMenuHandle(tvName: string) {
        this.props.onClose();
        RemoveFromWorkspaceLayerEvent.fireRemoveTv(tvName);
    }

    actionKey(action: string, tvName: string) {
        return `${action}_${tvName}`;
    }

    handleOnClick = event => {
        const parsedKey = event.key.split('_');
        const action = parsedKey[0].trim();
        //check if is a valid action
        if (!ACTIONS.includes(action)) return;

        const tvName = parsedKey[1];
        const tvLoad = this.props.tvs.find(tvLoad => tvLoad.tvName === tvName);

        //check if is a valid tv
        if (!tvLoad) return;
        //close the context menu first
        this.props.onClose();

        switch (action) {
            case ACTION_SHOW_DETAILS:
                ModalHandler.showTvDetails(
                    tvLoad.tvName,
                    tvLoad.vsName,
                    this.getTvRegulationPeriods(tvLoad),
                    tvLoad.occupancyPeriods,
                );
                break;
            case ACTION_CREATE_NEW_REGULATION:
                ModalHandler.showRegulationEditor(tvName, 'tv');
                break;
            case ACTION_CREATE_NEW_HOTSPOT:
                ShowModalEvent.fireNotClosable(
                    <HotspotPanel tv={tvName} refLoc={tvLoad.refLoc} />,
                    HotspotPanel.HOTSPOT_PANEL_WIDTH_EM,
                );
                break;
            case ACTION_FIND_SOLUTION:
                ModalHandler.showFindSolution([{ tvId: tvName }]);
                break;
            case ACTION_ADD_NOTE:
                OpenTvMonitoredEvents.fire(
                    MonitoredEventType.NOTE,
                    tvLoad.tvName,
                    tvLoad.refLoc,
                    [tvLoad.vsName],
                    tvLoad.monitoredMeasures.map(m => m.measure.measureId.id),
                );
                break;
            case ACTION_ADD_EVENT:
                OpenTvMonitoredEvents.fire(
                    MonitoredEventType.WEATHER_EVENT,
                    tvLoad.tvName,
                    tvLoad.refLoc,
                    [tvLoad.vsName],
                    tvLoad.monitoredMeasures.map(m => m.measure.measureId.id),
                );
                break;
            case ACTION_CREATE_CUSTOM_LAYER:
                this.showCustomLayerNamePopup(tvName);
                break;
        }
    };

    getTvRegulationPeriods = (tvLoadProps: TVLoadProps): Array<RegulationPeriods> => {
        return this.getMonitoredMeasuresForTv(tvLoadProps.monitoredMeasures, tvLoadProps.tvName).map(
            monitoredMeasure => {
                const regulation: RegulationOrMcdmOnlyDto = monitoredMeasure.measure;
                return MeasureUtils.getRegulationPeriods(regulation);
            },
        );
    };

    getMonitoredMeasuresForTv = memoizeOne((monitoredMeasures: Array<MonitoredMeasure>, tvId: string) => {
        return monitoredMeasures?.filter(
            monitoredMeasure => MeasureUtils.getTvLocation(monitoredMeasure.measure).id === tvId,
        );
    });

    showCustomLayerNamePopup(tvName: string) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: [tvName],
                    selected: [tvName],
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };
}

export const MapTVContextMenu = connector(MapTVContextMenuComponent);
